"use client";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, type ComponentType, type SVGProps } from "react";
import {
  Activity,
  BarChart3,
  FileHeart,
  MessageSquareHeart,
  ShieldCheck,
  Stethoscope,
  Users,
  Menu,
  X,
} from "lucide-react";
import ScrollToTopButton from "@/components/landing-page/scroll-to-top-button";
import { WhatsAppIcon } from "@/components/icons/whatsapp-icon";
import { getWhatsAppUrl } from "@/constants/contact";

const NAV = [
  { label: "Início", href: "#inicio" },
  { label: "Diferenciais", href: "#diferenciais" },
  { label: "Jornada", href: "#jornada" },
  { label: "Resultados", href: "#resultados" },
  { label: "Contato", href: "#contato" },
] as const;

const HIGHLIGHTS = [
  "Planos personalizados para cada paciente",
  "Monitoramento contínuo do pré ao pós-operatório",
  "Comunicação segura entre equipes multidisciplinares",
] as const;

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const DIF: { title: string; description: string; icon: Icon }[] = [
  { title: "Cuidado integral", description: "Fluxos pré, intra e pós com alertas.", icon: FileHeart },
  { title: "Monitoramento contínuo", description: "Indicadores em tempo real para ação.", icon: Activity },
  { title: "Colaboração segura", description: "Comunicação estruturada e centralizada.", icon: MessageSquareHeart },
];

const STAGES: { title: string; description: string; icon: Icon }[] = [
  { title: "Pré-operatório", description: "Avaliações e preparo do paciente.", icon: ShieldCheck },
  { title: "Intraoperatório", description: "Checklists e registro de dados críticos.", icon: Stethoscope },
  { title: "Pós-operatório", description: "Planos personalizados e alertas.", icon: BarChart3 },
];

const FEATURES: { title: string; desc: string; icon: Icon }[] = [
  { title: "Engajamento elevado", desc: "Responsabilidade compartilhada.", icon: Users },
  { title: "Governança clínica", desc: "Protocolos e aderência.", icon: ShieldCheck },
  { title: "Indicadores centralizados", desc: "Dashboards e relatórios.", icon: BarChart3 },
  { title: "Ações proativas", desc: "Alertas que antecipam riscos.", icon: Activity },
];

const WA = getWhatsAppUrl();

function useScrolled() {
  const [s, setS] = useState(false);
  useEffect(() => {
    const fn = () => setS(window.scrollY > 10);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return s;
}
function SectionTitle({
  tag,
  title,
  subtitle,
}: {
  tag?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="text-center">
      {tag && (
        <span className="inline-block text-xs font-semibold uppercase tracking-wide bg-sky-100 text-sky-700 px-4 py-1 rounded-full">
          {tag}
        </span>
      )}
      <h2 className="mt-6 text-3xl sm:text-4xl font-bold">{title}</h2>
      {subtitle && <p className="mt-3 text-slate-600">{subtitle}</p>}
    </div>
  );
}

function Pill({
  icon: Icon,
  title,
  desc,
}: {
  icon: Icon;
  title: string;
  desc: string;
}) {
  return (
    <div className="group relative rounded-3xl border border-sky-200 bg-white p-6 shadow transition hover:-translate-y-1">
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-sky-100/50 blur-2xl group-hover:bg-sky-200/60" />
      <div className="relative">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-700 text-white grid place-items-center">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="mt-4 font-bold">{title}</h3>
        <p className="mt-2 text-sm text-slate-600">{desc}</p>
      </div>
    </div>
  );
}

export default function HomePageLean() {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);

  return (
    <main className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-900 scroll-smooth" id="topo">
      <nav
        className={`fixed inset-x-0 top-0 z-50 w-full max-w-[100%] transition-all duration-300 ${
          scrolled
            ? "border-b border-sky-100 bg-white/90 shadow-md backdrop-blur-md"
            : "border-0 bg-gradient-to-r from-sky-900 via-sky-800 to-sky-700"
        }`}
      >
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Link
            href="/"
            className={`flex items-center gap-2 ${
              scrolled ? "text-sky-800" : "text-white"
            }`}
          >
            <Image
              src="/images/heart_image.png"
              alt="Logo Heart"
              width={28}
              height={28}
              className="h-7 w-7"
            />
            <span className="font-bold text-xl">Heart</span>
          </Link>
          <div className="hidden md:flex items-center gap-2">
            {NAV.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  scrolled
                    ? "text-slate-600 hover:bg-sky-50"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              href="/tutorial-paciente"
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                scrolled
                  ? "text-sky-700 hover:bg-sky-50"
                  : "text-white hover:bg-white/10"
              }`}
            >
              Sou Paciente
            </Link>
            <Link
              href="/login"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                scrolled
                  ? "bg-sky-700 text-white hover:bg-sky-800"
                  : "bg-white text-sky-800 hover:bg-sky-50"
              }`}
            >
              Acessar Plataforma
            </Link>
          </div>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            className={`md:hidden p-2 rounded transition-colors ${
              scrolled
                ? "text-slate-600 hover:bg-sky-50"
                : "text-white hover:bg-white/10"
            }`}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        <div
          className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
            open
              ? "max-h-[100dvh] opacity-100"
              : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          <div
            className={`${
              scrolled ? "bg-white/95 backdrop-blur-sm" : "bg-sky-900/95 backdrop-blur-sm"
            } space-y-2 px-4 pb-4 ${open ? "border-t" : "border-t-0"} ${
              scrolled ? "border-sky-100" : "border-white/10"
            }`}
          >
            {NAV.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2 rounded ${
                  scrolled
                    ? "text-slate-700 hover:bg-sky-50"
                    : "text-white hover:bg-white/10"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              href="/tutorial-paciente"
              onClick={() => setOpen(false)}
              className={`block px-4 py-2 rounded font-medium ${
                scrolled
                  ? "text-sky-700 hover:bg-sky-50"
                  : "text-white hover:bg-white/10"
              }`}
            >
              Sou Paciente
            </Link>
            <Link
              href="/login"
              onClick={() => setOpen(false)}
              className={`block text-center px-4 py-2 rounded font-semibold ${
                scrolled
                  ? "bg-sky-700 text-white"
                  : "bg-white text-sky-800"
              }`}
            >
              Acessar Plataforma
            </Link>
          </div>
        </div>
      </nav>
      <div className="h-14" aria-hidden />
      <section
        id="inicio"
        className="relative bg-gradient-to-b from-sky-50 via-white to-white"
      >
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative order-1 lg:order-2 max-w-md mx-auto lg:max-w-none">
            <div className="absolute -inset-10 blur-3xl bg-sky-200/40 rounded-full animate-pulse" />
            <Image
              src="/images/imageMedico.png"
              alt="Profissional de saúde utilizando a plataforma Heart em um tablet"
              width={680}
              height={680}
              priority
              className="relative z-10 w-full h-auto object-contain drop-shadow-2xl"
            />
          </div>
          <div className="order-2 lg:order-1">
            <span className="inline-block text-xs font-semibold uppercase tracking-wide border border-sky-200 bg-white text-sky-800 px-4 py-1 rounded-full">
              Plataforma digital de cuidado médico
            </span>
            <h1 className="mt-6 text-3xl sm:text-4xl lg:text-[2.8rem] font-bold leading-tight">
              Transforme a jornada cirúrgica com o Heart
            </h1>
            <p className="mt-4 text-slate-600 text-lg">
              Gestão do cuidado cirúrgico com foco em integralidade,
              monitoramento e colaboração segura.
            </p>
            <Link
              href="/tutorial-paciente"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/90 px-4 py-2 text-sm font-semibold text-sky-800 shadow-sm transition hover:bg-sky-50 hover:border-sky-300"
            >
              Sou paciente? Veja como usar o app
            </Link>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {HIGHLIGHTS.map((h) => (
                <div
                  key={h}
                  className="rounded-2xl border border-sky-100 bg-white/80 p-4 text-sm shadow-sm"
                >
                  {h}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section id="diferenciais" className="py-20 bg-white">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <SectionTitle
            tag="Por que escolher o Heart?"
            title="Padronização, segurança e humanização"
            subtitle="Tecnologia para manter o foco no paciente."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DIF.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="group h-[220px] rounded-3xl border border-sky-100 bg-white p-6 shadow hover:shadow-lg transition"
              >
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-700 text-white grid place-items-center">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-bold text-lg">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-3 group-hover:line-clamp-none">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="jornada" className="py-20 text-white bg-gradient-to-r from-sky-900 via-sky-800 to-sky-700">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-wide border border-white/20 px-4 py-1 rounded-full text-white/80">
              Jornada completa
            </span>
            <h2 className="mt-6 text-3xl sm:text-4xl font-bold">
              Do preparo à recuperação
            </h2>
            <p className="mt-3 text-white/80">
              Protocolos, indicadores e equipes conectadas.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STAGES.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="rounded-3xl bg-white/10 p-6 backdrop-blur shadow"
              >
                <Icon className="h-10 w-10 text-sky-200" />
                <h3 className="mt-4 text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-white/80">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="resultados" className="py-20 bg-sky-50/70">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <SectionTitle
            tag="Benefícios"
            title="Humanize o cuidado e ganhe precisão"
            subtitle="Eficiência e padronização com foco em desfechos."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <Pill key={f.title} icon={f.icon} title={f.title} desc={f.desc} />
            ))}
          </div>
        </div>
      </section>
      <section id="contato" className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-[2.4rem] font-bold">
            Pronto para elevar a experiência cirúrgica?
          </h2>
          <p className="mt-3 text-slate-600">
            Mostramos como o Heart integra equipes e transforma processos.
          </p>
          <a
            href={WA}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-sky-700 hover:bg-sky-800 text-white px-6 py-3 text-sm font-semibold transition-colors shadow-lg hover:shadow-xl"
          >
            Entrar em contato <WhatsAppIcon className="h-4 w-4" />
          </a>
        </div>
      </section>

      <footer className="border-t border-sky-100 bg-white py-8">
        <div className="mx-auto max-w-5xl px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <p>
            © {new Date().getFullYear()}{" "}
            <Link href="#topo" className="font-semibold text-sky-700 hover:underline">
              Heart
            </Link>
            . Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/tutorial-paciente" className="hover:text-sky-600 hover:underline">
              Sou paciente
            </Link>
            <Link href="/privacidade" className="hover:text-sky-600 hover:underline">
              Política de Privacidade
            </Link>
            <Link href="/login" className="hover:text-sky-600 hover:underline">
              Acesso ao sistema
            </Link>
          </div>
        </div>
      </footer>
      <ScrollToTopButton />
    </main>
  );
}
